import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Settings, LogOut, QrCode, Calendar, Utensils, 
  Printer, Download, CheckCircle
} from "lucide-react";
import { getQRData } from "@/lib/api";

// QR Code generation using Google Charts API (simple solution)
const generateQRCodeURL = (data, size = 200) => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;
};

export default function AdminQRCodes() {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    // Check auth
    if (sessionStorage.getItem("adminAuth") !== "true") {
      navigate("/admin");
      return;
    }

    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getQRData();
      setHotels(res.data);
    } catch (error) {
      console.error("Error loading QR data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const getBaseURL = () => {
    // Use the current origin or the backend URL for QR codes
    return window.location.origin;
  };

  const generateReservationURL = (hotelId, cabinId) => {
    return `${getBaseURL()}/?hotel=${hotelId}&cabin=${cabinId}`;
  };

  const handlePrintAll = () => {
    // Flatten all cabins
    const allCabins = filteredHotels.flatMap(hotel => 
      hotel.cabins.map(cabin => ({ hotel, cabin }))
    );
    
    // Group into pairs for 2 per page
    const pages = [];
    for (let i = 0; i < allCabins.length; i += 2) {
      pages.push(allCabins.slice(i, i + 2));
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>La Olla QR Codes - ${selectedHotel ? hotels.find(h => h.id === selectedHotel)?.name : "All Hotels"}</title>
          <style>
            @page {
              size: landscape;
              margin: 0.3in;
            }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              background: white;
              font-size: 11pt;
            }
            .page {
              width: 10.5in;
              height: 7.5in;
              padding: 0.25in;
              page-break-after: always;
              box-sizing: border-box;
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 0.4in;
              background: white;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .card {
              width: 3.8in;
              height: 7in;
              padding: 0.15in 0.25in;
              border: 2px solid #666;
              border-radius: 24px;
              background: linear-gradient(135deg, #e8e8e8 0%, #d4d4d4 25%, #c8c8c8 50%, #d4d4d4 75%, #e8e8e8 100%);
              display: flex;
              flex-direction: column;
            }
            .brand {
              text-align: center;
              margin-bottom: 0.1in;
            }
            .brand h1 { 
              font-size: 24pt; 
              color: #C04000; 
              font-family: Georgia, 'Times New Roman', serif;
              font-weight: bold;
              margin-bottom: 2px;
            }
            .brand .slogan { 
              font-size: 9pt; 
              color: #555; 
              font-style: italic; 
            }
            .meal-card {
              background: rgba(255,255,255,0.6);
              border: 1px solid rgba(0,0,0,0.15);
              border-radius: 10px;
              padding: 0.1in 0.12in;
              margin: 0 auto 0.1in auto;
              width: 3in;
            }
            .meal-card h2 {
              font-size: 11pt;
              text-align: center;
              margin-bottom: 0.06in;
              color: #333;
            }
            .meal-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px solid rgba(0,0,0,0.1);
              font-size: 10pt;
              color: #444;
            }
            .meal-row:last-of-type { border-bottom: none; }
            .meal-name { font-weight: 600; color: #222; }
            .meal-price { color: #C04000; font-weight: bold; }
            .meal-note {
              text-align: center;
              font-size: 9pt;
              color: #666;
              font-style: italic;
              margin-top: 0.05in;
              padding-top: 0.05in;
              border-top: 1px solid rgba(0,0,0,0.1);
            }
            .meal-desc {
              font-size: 9pt;
              color: #444;
              text-align: center;
              margin-bottom: 0.08in;
              line-height: 1.4;
            }
            .meal-desc-line {
              display: block;
            }
            .policies {
              font-size: 9pt;
              color: #555;
              line-height: 1.4;
              margin-bottom: 0.05in;
              text-align: center;
            }
            .policies p {
              margin-bottom: 2px;
            }
            .policies strong {
              color: #222;
            }
            .policies .highlight {
              color: #C04000;
            }
            .qr-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 0.12in;
            }
            .scan-text {
              font-size: 9pt;
              color: #C04000;
              font-weight: 600;
            }
            .qr-code {
              display: inline-block;
              background: white;
              padding: 6px;
              border-radius: 8px;
              border: 1px solid #ccc;
            }
            .qr-code img {
              width: 1in;
              height: 1in;
            }
            .cabin-info {
              text-align: center;
            }
            .hotel-name {
              font-size: 10pt;
              color: #555;
              margin-bottom: 2px;
            }
            .cabin-name {
              font-size: 16pt;
              font-weight: bold;
              color: #C04000;
            }
            @media print {
              body { 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact;
              }
              .page { page-break-after: always; }
              .page:last-child { page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          ${pages.map((pageCabins) => `
            <div class="page">
              ${pageCabins.map(({ hotel, cabin }) => {
                const qrURL = generateReservationURL(hotel.id, cabin.id);
                return `
                  <div class="card">
                    <div class="brand">
                      <h1>La Olla</h1>
                      <p class="slogan">"Where the day's adventures become tonight's stories."</p>
                    </div>
                    
                    <div class="meal-card">
                      <h2>Meal Times & Prices</h2>
                      <div class="meal-row">
                        <span class="meal-name">Breakfast</span>
                        <span>6:00 - 8:00 AM • <span class="meal-price">$12</span></span>
                      </div>
                      <div class="meal-row">
                        <span class="meal-name">Lunch</span>
                        <span>12:30 - 1:30 PM • <span class="meal-price">$15</span></span>
                      </div>
                      <div class="meal-row">
                        <span class="meal-name">Dinner</span>
                        <span>6:30 - 7:30 PM • <span class="meal-price">$18</span></span>
                      </div>
                      <p class="meal-note">Guests may arrive anytime within the meal window.</p>
                    </div>
                    
                    <p class="meal-desc">
                      <span class="meal-desc-line">Breakfast has a menu to order from.</span>
                      <span class="meal-desc-line">Lunch & Dinner are preplanned meals</span>
                      <span class="meal-desc-line">for all the family cabins.</span>
                      <span class="meal-desc-line">If you have a diet, allergy or special request</span>
                      <span class="meal-desc-line">we will be happy to accommodate.</span>
                    </p>
                    
                    <div class="policies">
                      <p><strong>Reservations & Cancellations:</strong></p>
                      <p>2+ hours before meal time</p>
                      <p style="margin-top: 4px;">Room service: <span class="highlight">+$2.50/person</span></p>
                      <p>Tab paid in restaurant before departure.</p>
                      <p>We accept <strong>cash, PayPal & Credit Card</strong>.</p>
                    </div>
                    
                    <div class="qr-section">
                      <p class="scan-text">Scan to make a reservation</p>
                      <div class="qr-code">
                        <img src="${generateQRCodeURL(qrURL, 140)}" alt="QR Code" />
                      </div>
                      <div class="cabin-info">
                        <p class="hotel-name">${hotel.name}</p>
                        <p class="cabin-name">${cabin.name}</p>
                      </div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          `).join('')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredHotels = selectedHotel 
    ? hotels.filter(h => h.id === selectedHotel)
    : hotels;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-qr-codes">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold">QR Code Generator</h1>
                <p className="text-sm text-muted-foreground">La Olla</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-card/50 border-b border-border no-print">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            <Link to="/admin/dashboard">
              <Button variant="ghost" data-testid="nav-dashboard">
                <Utensils className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Link to="/admin/reservations">
              <Button variant="ghost" data-testid="nav-reservations">
                <Calendar className="w-4 h-4 mr-2" />
                Reservations
              </Button>
            </Link>
            <Link to="/admin/qr-codes">
              <Button variant="ghost" className="bg-primary/10 text-primary" data-testid="nav-qr-codes">
                <QrCode className="w-4 h-4 mr-2" />
                QR Codes
              </Button>
            </Link>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Controls */}
        <Card className="bg-card border-border mb-6 no-print">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Generate QR Codes
            </CardTitle>
            <CardDescription>
              Generate QR codes for each cabin. Guests scan these to make reservations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <Select
                value={selectedHotel || "all"}
                onValueChange={(val) => setSelectedHotel(val === "all" ? null : val)}
              >
                <SelectTrigger className="w-[250px] bg-background/50" data-testid="hotel-select">
                  <SelectValue placeholder="Select hotel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hotels</SelectItem>
                  {hotels.map((hotel) => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      {hotel.name} ({hotel.cabins.length} cabins)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handlePrintAll}
                className="bg-primary hover:bg-primary/90"
                data-testid="print-all-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print All QR Codes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* QR Codes Grid */}
        <div id="print-content">
          {loading ? (
            <div className="text-center py-12">
              <QrCode className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
              <p className="text-muted-foreground">Loading QR codes...</p>
            </div>
          ) : (
            (() => {
              // Flatten all cabins into a single array for printing
              const allCabins = filteredHotels.flatMap(hotel => 
                hotel.cabins.map(cabin => ({ hotel, cabin }))
              );
              
              // Group into pages of 6
              const pages = [];
              for (let i = 0; i < allCabins.length; i += 6) {
                pages.push(allCabins.slice(i, i + 6));
              }
              
              return (
                <>
                  {/* Screen display - grouped by hotel */}
                  <div className="no-print space-y-8">
                    {filteredHotels.map((hotel) => (
                      <div key={hotel.id} data-testid={`hotel-section-${hotel.id}`}>
                        <h2 className="font-display text-2xl font-bold mb-4 text-foreground">
                          {hotel.name}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {hotel.cabins.map((cabin) => {
                            const qrURL = generateReservationURL(hotel.id, cabin.id);
                            return (
                              <Card
                                key={`${hotel.id}-${cabin.id}`}
                                className="bg-card border-border text-center"
                                data-testid={`qr-card-${hotel.id}-${cabin.id}`}
                              >
                                <CardContent className="pt-4">
                                  <p className="text-sm text-muted-foreground mb-1">
                                    {hotel.name}
                                  </p>
                                  <p className="text-lg font-bold text-primary mb-3">
                                    {cabin.name}
                                  </p>
                                  <div className="bg-white p-2 rounded-lg inline-block mb-3">
                                    <img
                                      src={generateQRCodeURL(qrURL, 150)}
                                      alt={`QR Code for ${hotel.name} - ${cabin.name}`}
                                      className="w-[150px] h-[150px]"
                                      loading="lazy"
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground font-display italic">
                                    La Olla
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Scan to make a reservation
                                  </p>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Print layout - 6 per page */}
                  <div className="print-only" style={{ display: 'none' }}>
                    {pages.map((pageCabins, pageIndex) => (
                      <div key={pageIndex} className="page">
                        <div className="page-header">
                          <h1>La Olla</h1>
                          <p>"Where the day's adventures become tonight's stories."</p>
                        </div>
                        <div className="qr-grid">
                          {pageCabins.map(({ hotel, cabin }) => {
                            const qrURL = generateReservationURL(hotel.id, cabin.id);
                            return (
                              <div key={`${hotel.id}-${cabin.id}`} className="qr-card">
                                <p className="hotel-name">{hotel.name}</p>
                                <p className="cabin-name">{cabin.name}</p>
                                <div className="qr-image">
                                  <img
                                    src={generateQRCodeURL(qrURL, 120)}
                                    alt={`QR Code for ${hotel.name} - ${cabin.name}`}
                                    style={{ width: '120px', height: '120px' }}
                                  />
                                </div>
                                <p className="brand">La Olla</p>
                                <p className="scan-text">Scan to reserve</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()
          )}
        </div>

        {/* Summary */}
        <Card className="bg-card border-border mt-6 no-print">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="w-5 h-5" />
              <span>
                {filteredHotels.reduce((sum, h) => sum + h.cabins.length, 0)} QR codes ready to print
              </span>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
